import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Form, InputGroup, Spinner, Table } from 'react-bootstrap';
import { api, API_URL } from '../../lib/api';
import { useToast } from '../../context/ToastContext';
import { useConfirm } from '../../context/ConfirmContext';
import { formatarDataHora } from './formatos';

// "Outros documentos" do imóvel (aba Confidencial e Captação): matrícula, escritura, contrato, laudo, PDF, Word, fotos de documento...
// NÃO são publicados — ficam num armazenamento privado e só abrem com login. Excluir o imóvel apaga tudo junto.
function tamanhoLegivel(bytes) {
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1).replace('.', ',')} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

export default function DocumentosImovel({ publicId, podeEditar }) {
  const toast = useToast();
  const confirmar = useConfirm();
  const seletor = useRef(null);
  const [docs, setDocs] = useState([]);
  const [limites, setLimites] = useState(null);
  const [carregando, setCarregando] = useState(Boolean(publicId));
  const [enviando, setEnviando] = useState(null); // { atual, total } | null
  const [editando, setEditando] = useState(null); // { id, descricao }

  const carregar = useCallback(() => {
    if (!publicId) return;
    api
      .get(`/imoveis/${publicId}/documentos`)
      .then((r) => { setDocs(r.data); setLimites(r.limites); })
      .catch((err) => toast.erro(err.message || 'Não foi possível carregar os documentos'))
      .finally(() => setCarregando(false));
  }, [publicId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(carregar, [carregar]);

  async function enviar(arquivos) {
    const lista = [...arquivos];
    if (!lista.length) return;
    let ok = 0;
    for (let i = 0; i < lista.length; i++) {
      setEnviando({ atual: i + 1, total: lista.length });
      const dados = new FormData();
      dados.append('arquivo', lista[i]);
      try {
        await api.upload(`/imoveis/${publicId}/documentos`, dados);
        ok++;
      } catch (err) {
        toast.erro(`${lista[i].name}: ${err.message || 'não foi possível enviar'}`);
      }
    }
    setEnviando(null);
    if (seletor.current) seletor.current.value = '';
    if (ok) toast.sucesso(ok === 1 ? 'Documento anexado.' : `${ok} documentos anexados.`);
    carregar();
  }

  async function salvarDescricao() {
    try {
      await api.put(`/imoveis/${publicId}/documentos/${editando.id}`, { descricao: editando.descricao });
      setEditando(null);
      carregar();
    } catch (err) {
      toast.erro(err.message || 'Não foi possível salvar a descrição');
    }
  }

  async function excluir(doc) {
    if (!(await confirmar({ titulo: 'Excluir documento', mensagem: `Excluir "${doc.nome}"? Não dá para desfazer.`, textoConfirmar: 'Excluir', variante: 'perigo' }))) return;
    try {
      await api.delete(`/imoveis/${publicId}/documentos/${doc.id}`);
      toast.sucesso('Documento excluído.');
      carregar();
    } catch (err) {
      toast.erro(err.message || 'Não foi possível excluir');
    }
  }

  if (!publicId) return <div className="text-muted small">Salve o imóvel primeiro para poder anexar documentos.</div>;

  return (
    <div>
      <Alert variant="secondary" className="py-2 small">
        Documentos <b>não publicados</b>: matrícula, escritura, contratos, laudos, fotos de documentos... Ficam guardados com segurança dentro deste imóvel e só abrem
        para quem está logado. Se o imóvel for excluído, os documentos são excluídos junto.
      </Alert>

      {podeEditar && (
        <div className="mb-3">
          <Form.Control
            ref={seletor}
            type="file"
            multiple
            disabled={Boolean(enviando)}
            accept={limites ? limites.extensoes.map((e) => `.${e}`).join(',') : undefined}
            onChange={(e) => enviar(e.target.files)}
          />
          <Form.Text muted>
            {limites ? `Até ${limites.tamanhoMaxMb} MB por arquivo e ${limites.maximo} documentos por imóvel. ` : ''}
            PDF, Word, Excel, PowerPoint, LibreOffice, RTF, TXT, CSV e imagens.
          </Form.Text>
          {enviando && (
            <div className="mt-2 small">
              <Spinner size="sm" className="me-2" />
              Enviando {enviando.atual} de {enviando.total}...
            </div>
          )}
        </div>
      )}

      {carregando ? (
        <Spinner size="sm" />
      ) : docs.length === 0 ? (
        <div className="text-muted">Nenhum documento anexado.</div>
      ) : (
        <Table responsive hover size="sm" className="align-middle mb-0">
          <thead>
            <tr>
              <th>Documento</th>
              <th>Descrição</th>
              <th className="text-nowrap">Tamanho</th>
              <th className="text-nowrap">Anexado</th>
              {podeEditar && <th />}
            </tr>
          </thead>
          <tbody>
            {docs.map((d) => (
              <tr key={d.id}>
                <td>
                  <a href={`${API_URL}/imoveis/${publicId}/documentos/${d.id}/arquivo`} target="_blank" rel="noreferrer">{d.nome}</a>
                </td>
                <td style={{ minWidth: 180 }}>
                  {editando?.id === d.id ? (
                    <InputGroup size="sm">
                      <Form.Control
                        autoFocus
                        value={editando.descricao}
                        maxLength={300}
                        onChange={(e) => setEditando({ ...editando, descricao: e.target.value })}
                        onKeyDown={(e) => { if (e.key === 'Enter') salvarDescricao(); if (e.key === 'Escape') setEditando(null); }}
                      />
                      <Button variant="success" onClick={salvarDescricao}>Salvar</Button>
                      <Button variant="outline-secondary" onClick={() => setEditando(null)}>Cancelar</Button>
                    </InputGroup>
                  ) : (
                    <span className="text-muted">{d.descricao || '—'}</span>
                  )}
                </td>
                <td className="text-nowrap small">{tamanhoLegivel(d.tamanho)}</td>
                <td className="text-nowrap small">
                  {formatarDataHora(d.criadoEm)}
                  {d.anexadoPor ? <div className="text-muted">{d.anexadoPor}</div> : null}
                </td>
                {podeEditar && (
                  <td className="text-nowrap text-end">
                    <Button size="sm" variant="outline-secondary" className="me-1" onClick={() => setEditando({ id: d.id, descricao: d.descricao })}>Descrição</Button>
                    <Button size="sm" variant="outline-danger" onClick={() => excluir(d)}>Excluir</Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
}
